import { motion } from 'framer-motion'
import { 
  Printer, 
  Box, 
  TrendingUp, 
  Clock, 
  Sparkles,
  ArrowRight,
  Play,
  CheckCircle,
  Zap,
  FolderOpen,
  Loader2,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useProfile } from '@/hooks/useProfile'
import { useProjects } from '@/hooks/useProjects'
import { useDesigns } from '@/hooks/useDesigns'
import { usePrinters } from '@/hooks/usePrinters'
import { usePrintJobs } from '@/hooks/usePrintJobs'
import { formatDuration, formatDistanceToNow } from '@/lib/utils'
import type { ViewType } from '@/pages/dashboard/DashboardApp'

interface DashboardProps {
  onNavigate: (view: ViewType) => void
  onSelectDesign?: (designId: string | null, designUrl: string | null) => void
}

const quickActions = [
  { id: 'text-to-3d', label: 'Text to 3D', description: 'Generate from description', icon: Sparkles, gradient: true },
  { id: 'new-design', label: 'New Design', description: 'Start from scratch', icon: Box },
  { id: 'print-queue', label: 'Print Queue', description: 'Manage print jobs', icon: Printer },
]

export default function Dashboard({ onNavigate, onSelectDesign }: DashboardProps) {
  const { profile } = useProfile()
  const { projects, isLoading: projectsLoading } = useProjects()
  const { designs, isLoading: designsLoading } = useDesigns()
  const { activePrinter } = usePrinters()
  const { currentJob, successRate, totalJobs } = usePrintJobs()
  
  const userName = profile?.full_name?.split(' ')[0] || profile?.display_name || 'there'
  
  const stats = [
    { label: 'Projects', value: projects.length.toString(), change: 'Active', icon: FolderOpen, color: 'primary' },
    { label: 'Designs', value: designs.length.toString(), change: 'Total', icon: Box, color: 'ai' },
    { label: 'Print Success', value: successRate !== null ? `${successRate}%` : '-', change: totalJobs > 0 ? `${totalJobs} jobs` : 'No prints yet', icon: TrendingUp, color: 'success' },
    { label: 'AI Credits', value: profile?.ai_generations_used?.toString() || '0', change: 'Used', icon: Clock, color: 'warning' },
  ]

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Welcome Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Welcome back, {userName}</h1>
          <p className="text-surface-400 mt-1">Here's what's happening with your prints and designs.</p>
        </div>
        <Button variant="ai" onClick={() => onNavigate('design')}>
          <Sparkles className="w-4 h-4" />
          Start Creating
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="relative overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-surface-400">{stat.label}</p>
                    <p className="text-3xl font-bold text-white mt-1">{stat.value}</p>
                    <p className={`text-xs mt-1 ${
                      stat.color === 'success' ? 'text-success-500' : 
                      stat.color === 'ai' ? 'text-ai-400' :
                      stat.color === 'warning' ? 'text-warning-500' :
                      'text-primary-400'
                    }`}>
                      {stat.change}
                    </p>
                  </div>
                  <div className={`p-3 rounded-xl ${
                    stat.color === 'success' ? 'bg-success-500/10' : 
                    stat.color === 'ai' ? 'bg-ai-500/10' :
                    stat.color === 'warning' ? 'bg-warning-500/10' :
                    'bg-primary-500/10'
                  }`}>
                    <stat.icon className={`w-6 h-6 ${
                      stat.color === 'success' ? 'text-success-500' : 
                      stat.color === 'ai' ? 'text-ai-400' :
                      stat.color === 'warning' ? 'text-warning-500' :
                      'text-primary-400'
                    }`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Print Monitor */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Active Print</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('print')}>
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {currentJob ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-32 h-32 bg-surface-800 rounded-xl flex items-center justify-center">
                      <Box className="w-12 h-12 text-surface-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">{currentJob.name}</h3>
                      <p className="text-sm text-surface-400">{activePrinter?.name || 'Printer'}</p>
                      
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-surface-400">Progress</span>
                          <span className="text-white font-medium">{currentJob.progress_percent || 0}%</span>
                        </div>
                        <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-primary-500 to-ai-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${currentJob.progress_percent || 0}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-surface-500">Layer</p>
                          <p className="text-white font-medium">
                            {currentJob.current_layer || 0}/{currentJob.total_layers || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-surface-500">Time Left</p>
                          <p className="text-white font-medium">
                            {currentJob.estimated_time_seconds && currentJob.elapsed_time_seconds 
                              ? formatDuration(currentJob.estimated_time_seconds - currentJob.elapsed_time_seconds)
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-surface-500">Status</p>
                          <p className="text-success-400 font-medium flex items-center gap-1 capitalize">
                            <CheckCircle className="w-3 h-3" />
                            {currentJob.status}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-surface-800">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
                      <span className="text-surface-400">Printing</span>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Button variant="secondary" size="sm">Pause</Button>
                      <Button variant="ghost" size="sm">Cancel</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Printer className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400">No active prints</p>
                  <Button variant="primary" size="sm" className="mt-4" onClick={() => onNavigate('print')}>
                    Start a Print
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action) => (
              <button
                key={action.id}
                onClick={() => action.id === 'print-queue' ? onNavigate('print') : onNavigate('design')}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                  action.gradient 
                    ? 'bg-gradient-to-r from-ai-600/20 to-primary-600/20 border border-ai-500/30 hover:border-ai-400/50' 
                    : 'bg-surface-800 hover:bg-surface-700'
                }`}
              >
                <div className={`p-2.5 rounded-lg ${
                  action.gradient ? 'bg-ai-500/20' : 'bg-surface-700'
                }`}>
                  <action.icon className={`w-5 h-5 ${
                    action.gradient ? 'text-ai-400' : 'text-surface-300'
                  }`} />
                </div>
                <div className="text-left">
                  <p className={`font-medium ${action.gradient ? 'text-white' : 'text-surface-200'}`}>
                    {action.label}
                  </p>
                  <p className="text-xs text-surface-400">{action.description}</p>
                </div>
                {action.gradient && (
                  <Zap className="w-4 h-4 text-ai-400 ml-auto" />
                )}
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Recent Designs / Projects */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{designs.length > 0 ? 'Recent Designs' : 'Your Projects'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => onNavigate('projects')}>
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {designsLoading || projectsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : designs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {designs.slice(0, 4).map((design) => (
                <motion.div
                  key={design.id}
                  whileHover={{ y: -4 }}
                  className="group cursor-pointer"
                  onClick={() => onSelectDesign ? onSelectDesign(design.id, design.file_path || null) : onNavigate('design')}
                >
                  <div className="aspect-square bg-surface-800 rounded-xl overflow-hidden relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Box className="w-16 h-16 text-surface-600 group-hover:text-surface-500 transition-colors" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-surface-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <Button variant="primary" size="sm" className="w-full">
                        <Play className="w-4 h-4" /> Open
                      </Button>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 bg-surface-700 text-surface-300 text-xs font-medium rounded-full capitalize">
                        {design.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="font-medium text-surface-200 group-hover:text-white transition-colors">{design.name}</p>
                    <p className="text-sm text-surface-500">{formatDistanceToNow(design.updated_at)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.slice(0, 3).map((project) => (
                <motion.div
                  key={project.id}
                  whileHover={{ y: -4 }}
                  className="group cursor-pointer p-4 bg-surface-800 rounded-xl"
                  onClick={() => onNavigate('projects')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${project.color || '#6366f1'}20` }}
                    >
                      <FolderOpen className="w-5 h-5" style={{ color: project.color || '#6366f1' }} />
                    </div>
                    <div>
                      <p className="font-medium text-white">{project.name}</p>
                      <p className="text-xs text-surface-500">{formatDistanceToNow(project.updated_at)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderOpen className="w-12 h-12 text-surface-600 mx-auto mb-3" />
              <p className="text-surface-400 mb-4">No projects yet. Create your first one!</p>
              <Button onClick={() => onNavigate('projects')}>
                <Sparkles className="w-4 h-4" />
                Create Project
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
